// src/features/posteingang/services/posteingangService.ts
//
// Zugriff auf den Posteingang über die Netlify-Funktion posteingang. Als
// Ausweis dient die persönliche Anmeldung (App-Token aus dem Google-Login in
// localStorage 'authToken'), nicht das gemeinsame Datenbankkonto. Gleiches
// Muster wie die Simulation im Kundenportal (ContactsManager).

import { netlifyFetch } from '../../../shared/services/netlifyFetch';
import { PosteingangLesestand, PosteingangListe, PosteingangText, PosteingangVersandstand } from '../types';

export class PosteingangFehler extends Error {
  neuAnmelden: boolean;

  constructor(message: string, neuAnmelden = false) {
    super(message);
    this.name = 'PosteingangFehler';
    this.neuAnmelden = neuAnmelden;
  }
}

class PosteingangService {
  private async aufrufen(nutzlast: Record<string, unknown>): Promise<any> {
    const identitaet = localStorage.getItem('authToken');
    if (!identitaet) {
      throw new PosteingangFehler('Für den Posteingang ist die persönliche Anmeldung im Intranet erforderlich. Bitte neu anmelden.', true);
    }

    const res = await netlifyFetch('/.netlify/functions/posteingang', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${identitaet}` },
      body: JSON.stringify(nutzlast)
    });
    const daten = await res.json().catch(() => ({}));

    if (res.status === 401) {
      throw new PosteingangFehler('Deine Anmeldung ist abgelaufen. Bitte melde dich neu an.', true);
    }
    if (!res.ok || !daten.success) {
      throw new PosteingangFehler(daten.error || `HTTP ${res.status}`);
    }
    return daten;
  }

  async ladeListe(): Promise<PosteingangListe> {
    const daten = await this.aufrufen({ aktion: 'liste' });
    return {
      dateien: daten.dateien || [],
      letzter_lauf: daten.letzter_lauf || null,
      letzte_erfolgreiche_abholung: daten.letzte_erfolgreiche_abholung || null
    };
  }

  async oeffnungsLink(id: string): Promise<string> {
    const daten = await this.aufrufen({ aktion: 'oeffnen', id });
    if (!daten.url) {
      throw new PosteingangFehler('Die Datei konnte nicht geöffnet werden.');
    }
    return daten.url as string;
  }

  async ladeText(id: string): Promise<PosteingangText> {
    const daten = await this.aufrufen({ aktion: 'text', id });
    if (typeof daten.text !== 'string') {
      throw new PosteingangFehler('Der Text konnte nicht geladen werden.');
    }
    return { text: daten.text as string, gekuerzt: daten.gekuerzt === true };
  }

  async setzeGelesen(id: string, gelesen: boolean): Promise<PosteingangLesestand> {
    const daten = await this.aufrufen({ aktion: 'gelesen', id, gelesen });
    return {
      gelesen_am: daten.gelesen_am || null,
      gelesen_von: daten.gelesen_von || null
    };
  }

  // Sendet einen Brief als Mail an lohn@xtax.de, damit daraus ein Ticket
  // entsteht. Je Aufruf ein Brief; die Massenaktion ruft dies je Brief auf.
  async sendeAnLohn(id: string): Promise<PosteingangVersandstand> {
    const daten = await this.aufrufen({ aktion: 'an_lohn', id });
    return {
      gelesen_am: daten.gelesen_am || null,
      gelesen_von: daten.gelesen_von || null,
      weitergeleitet_am: daten.weitergeleitet_am || null,
      weitergeleitet_von: daten.weitergeleitet_von || null
    };
  }
}

// Singleton Instance
export const posteingangService = new PosteingangService();
